import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { NewPartnerForm } from "../../forms/NewPartnerForm/NewPartnerForm";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { Partner } from "../../models/Partner";
import { TransactionType } from "../../models/Transaction";
import { MercantileType, ParamSettings } from "../../models/ParamSettings";
import { getMercantileOutputTemplate } from "../../document-templates/MercantileOutputTemplate";
import { activeUser } from "../../services/AuthService";
import { srpsCalculation } from "../../services/SprsService";
import {
  getOneDocument,
  addNewDocument,
  updateOneDocument,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllSettings } from "../../services/SettingsService";
import "./NewDocumentOfOutputPage.scss";

export interface DocumentOfOutputPageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const NewDocumentOfOutputPage = (p: DocumentOfOutputPageProps) => {
  const { show, buttonSaveStyle, buttonNewPartnerStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [updating, setUpdating] = useState<boolean>(false);
  const [newDocumentOfOutput, setNewDocumentOfOutput] =
    useState<DocumentOfReceive>({
      id: id ?? "",
      warehouse:
        activeUser?.role?.name === RoleType.Warehouseman
          ? (cloneDeep(activeUser.warehouse) as Warehouse)
          : ({} as Warehouse),
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      type: TransactionType.Export,
      date: new Date(),
      time: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      srps: undefined,
      product: {} as Product,
      productWithCode: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "",
      vehicleNumber: "",
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      calculationMethod: true,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "",
      dispositionId: "",
      finishedDocument: false,
      finishedDocumentFirstTime: false,
      mercantile: true,
      input: false,
    });

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    [],
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [settings, setSettings] = useState<ParamSettings[]>([]);

  const [processing, setProcessing] = useState<boolean>(false);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [okButtonDisabled, setOkButtonDisabled] = useState<boolean>(false);
  const [productCategory, setProductCategory] = useState<ProductCategory>();
  const [pdfData, setPdfData] = useState<any>(null);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const unwrapArray = (resp: any): any[] => {
      const d = resp?.data;
      if (d?.result && Array.isArray(d.result)) return d.result;
      if (Array.isArray(d)) return d;
      return [];
    };

    (async () => {
      try {
        setProcessing(true);

        const [whResp, partnersResp, catResp, prodResp, settingsResp] =
          await Promise.all([
            getAllWarehouses(),
            getAllPartners(),
            getAllProductCategories(),
            getAllProducts(),
            getAllSettings(),
          ]);

        setWarehouses(unwrapArray(whResp) as Warehouse[]);
        setPartners(unwrapArray(partnersResp) as Partner[]);
        setProductCategories(unwrapArray(catResp) as ProductCategory[]);
        setProducts(unwrapArray(prodResp) as Product[]);
        setSettings(unwrapArray(settingsResp) as ParamSettings[]);
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju šifarnika"),
          life: 3000,
        });
      } finally {
        setProcessing(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) {
      setIsPdfReady(true);
      setProcessing(false);
      return;
    }

    (async () => {
      try {
        setProcessing(true);
        setIsPdfReady(false);

        const resp = await getOneDocument(id);
        const document: any = resp?.data?.result ?? resp?.data;

        if (document) {
          if (document.date) document.date = new Date(document.date);
          if (document.time) document.time = new Date(document.time);

          if (!document.warehouseman) {
            document.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
          }

          setNewDocumentOfOutput(document);
          setProductCategory(document.product?.category);
          setUpdating(true);
          setIsPdfReady(true);
        }
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju dokumenta"),
          life: 3000,
        });
      } finally {
        setProcessing(false);
      }
    })();
  }, [id]);

  // populate pdf when data changes
  useEffect(() => {
    let categoryType: MercantileType = MercantileType.Wheat;
    if (newDocumentOfOutput.product?.category?.name === "Uljana repica")
      categoryType = MercantileType.OiledBeet;
    if (newDocumentOfOutput.product?.category?.name === "Ječam")
      categoryType = MercantileType.Barley;
    if (newDocumentOfOutput.product?.category?.name === "Soja")
      categoryType = MercantileType.Soya;
    if (newDocumentOfOutput.product?.category?.name === "Suncokret")
      categoryType = MercantileType.Sunflower;
    if (newDocumentOfOutput.product?.category?.name === "Kukuruz")
      categoryType = MercantileType.Corn;
    if (
      isPdfReady &&
      settings.length > 0 &&
      Object.keys(newDocumentOfOutput).length > 0
    ) {
      const timer = setTimeout(() => {
        const newPdf = getMercantileOutputTemplate(
          categoryType,
          true,
          newDocumentOfOutput,
          settings,
          TransactionType.Export,
        );
        setPdfData(newPdf);
        setShowPdf(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [newDocumentOfOutput, isPdfReady, settings]);

  const toast = useRef(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    setOkButtonDisabled(true);
    let correctChanges: boolean = true;
    let error: string = "";
    switch (newDocumentOfOutput?.product?.category?.name) {
      case "Kukuruz": {
        const cornSettings = settings.find(
          (product) => product.mercantile === MercantileType.Corn,
        );
        if (
          (cornSettings?.impurityMax ?? 0) <
          (newDocumentOfOutput?.impurity ?? 0)
        ) {
          error = t("Primese kukuruza su veće od dozvoljenih");
          correctChanges = false;
        }
        if (
          (cornSettings?.defectMax ?? 0) < (newDocumentOfOutput?.defect ?? 0)
        ) {
          error = t("Defekt kukuruza je veći od dozvoljenog");
          correctChanges = false;
        }
        if (
          (cornSettings?.breakageMax ?? 0) <
          (newDocumentOfOutput?.breakage ?? 0)
        ) {
          error = t("Lom kukuruza je veći od dozvoljenog");
          correctChanges = false;
        }
        if (
          (cornSettings?.moistureMax ?? 0) <
          (newDocumentOfOutput?.moisture ?? 0)
        ) {
          error = t("Vlaga kukuruza je veća od dozvoljene");
          correctChanges = false;
        }
        break;
      }
      case "Soja": {
        const soyaSettings = settings.find(
          (product) => product.mercantile === MercantileType.Soya,
        );
        if (
          (soyaSettings?.moistureMax ?? 0) <
          (newDocumentOfOutput?.moisture ?? 0)
        ) {
          error = t("Vlaga soje je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (soyaSettings?.impurityMax ?? 0) <
          (newDocumentOfOutput?.impurity ?? 0)
        ) {
          error = t("Primese soje su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Suncokret": {
        const sunflowerSettings = settings.find(
          (product) => product.mercantile === MercantileType.Sunflower,
        );
        if (
          (sunflowerSettings?.moistureMax ?? 0) <
          (newDocumentOfOutput?.moisture ?? 0)
        ) {
          error = t("Vlaga suncokreta je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (sunflowerSettings?.impurityMax ?? 0) <
          (newDocumentOfOutput?.impurity ?? 0)
        ) {
          error = t("Primese suncokreta su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Uljana repica": {
        const oiledBeetSettings = settings.find(
          (product) => product.mercantile === MercantileType.OiledBeet,
        );
        if (
          (oiledBeetSettings?.moistureMax ?? 0) <
          (newDocumentOfOutput?.moisture ?? 0)
        ) {
          error = t("Vlaga uljane repice je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (oiledBeetSettings?.impurityMax ?? 0) <
          (newDocumentOfOutput?.impurity ?? 0)
        ) {
          error = t("Primese uljane repice su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Ječam": {
        const barleySettings = settings.find(
          (product) => product.mercantile === MercantileType.Barley,
        );
        if (
          (barleySettings?.hectoliterMax ?? 0) <
          (newDocumentOfOutput?.hectoliter ?? 0)
        ) {
          error = t("Hektolitar ječma je veći od dozvoljene");
          correctChanges = false;
        }
        if (
          (barleySettings?.moistureMax ?? 0) <
          (newDocumentOfOutput?.moisture ?? 0)
        ) {
          error = t("Vlaga ječma je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (barleySettings?.impurityMax ?? 0) <
          (newDocumentOfOutput?.impurity ?? 0)
        ) {
          error = t("Primese ječma su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Pšenica": {
        const wheatSettings = settings.find(
          (product) => product.mercantile === MercantileType.Wheat,
        );
        if (
          (wheatSettings?.hectoliterMax ?? 0) <
          (newDocumentOfOutput?.hectoliter ?? 0)
        ) {
          error = t("Hektolitar pšenice je veći od dozvoljene");
          correctChanges = false;
        }
        if (
          (wheatSettings?.moistureMax ?? 0) <
          (newDocumentOfOutput?.moisture ?? 0)
        ) {
          error = t("Vlaga pšenice je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (wheatSettings?.impurityMax ?? 0) <
          (newDocumentOfOutput?.impurity ?? 0)
        ) {
          error = t("Primese pšenice su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
    }

    if (correctChanges) {
      //calculation netto, srps, dryingCosts, dryWeight
      let categoryType: MercantileType = MercantileType.Wheat;
      if (newDocumentOfOutput.product?.category?.name === "Uljana repica")
        categoryType = MercantileType.OiledBeet;
      if (newDocumentOfOutput.product?.category?.name === "Ječam")
        categoryType = MercantileType.Barley;
      if (newDocumentOfOutput.product?.category?.name === "Soja")
        categoryType = MercantileType.Soya;
      if (newDocumentOfOutput.product?.category?.name === "Suncokret")
        categoryType = MercantileType.Sunflower;
      if (newDocumentOfOutput.product?.category?.name === "Kukuruz")
        categoryType = MercantileType.Corn;

      let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
      copyOfNewDocumentOfOutput.netto =
        newDocumentOfOutput.brutto !== undefined &&
        newDocumentOfOutput.tara !== undefined
          ? newDocumentOfOutput?.brutto - newDocumentOfOutput?.tara
          : 0;
      copyOfNewDocumentOfOutput.srps = Math.round(
        srpsCalculation(
          categoryType,
          newDocumentOfOutput.brutto !== undefined &&
            newDocumentOfOutput.tara !== undefined
            ? newDocumentOfOutput.brutto - newDocumentOfOutput.tara
            : 0,
          newDocumentOfOutput?.moisture ?? 0,
          newDocumentOfOutput.impurity ?? 0,
          newDocumentOfOutput.breakage ?? 0,
          newDocumentOfOutput.defect ?? 0,
          newDocumentOfOutput.hectoliter as number,
          newDocumentOfOutput.calculationMethod as boolean,
          settings,
          TransactionType.Export,
        )[0],
      );
      copyOfNewDocumentOfOutput.dryingCosts = Math.round(
        srpsCalculation(
          categoryType,
          newDocumentOfOutput.brutto !== undefined &&
            newDocumentOfOutput.tara !== undefined
            ? newDocumentOfOutput.brutto - newDocumentOfOutput.tara
            : 0,
          newDocumentOfOutput?.moisture ?? 0,
          newDocumentOfOutput.impurity ?? 0,
          newDocumentOfOutput.breakage ?? 0,
          newDocumentOfOutput.defect ?? 0,
          newDocumentOfOutput.hectoliter as number,
          newDocumentOfOutput.calculationMethod as boolean,
          settings,
          TransactionType.Export,
        )[11],
      );
      copyOfNewDocumentOfOutput.dryWeight =
        categoryType === MercantileType.Corn
          ? Math.round(
              srpsCalculation(
                categoryType,
                newDocumentOfOutput.brutto !== undefined &&
                  newDocumentOfOutput.tara !== undefined
                  ? newDocumentOfOutput.brutto - newDocumentOfOutput.tara
                  : 0,
                newDocumentOfOutput?.moisture ?? 0,
                newDocumentOfOutput.impurity ?? 0,
                newDocumentOfOutput.breakage ?? 0,
                newDocumentOfOutput.defect ?? 0,
                newDocumentOfOutput.hectoliter as number,
                newDocumentOfOutput.calculationMethod as boolean,
                settings,
                TransactionType.Export,
              )[0] -
                srpsCalculation(
                  categoryType,
                  newDocumentOfOutput.brutto !== undefined &&
                    newDocumentOfOutput.tara !== undefined
                    ? newDocumentOfOutput.brutto - newDocumentOfOutput.tara
                    : 0,
                  newDocumentOfOutput?.moisture ?? 0,
                  newDocumentOfOutput.impurity ?? 0,
                  newDocumentOfOutput.breakage ?? 0,
                  newDocumentOfOutput.defect ?? 0,
                  newDocumentOfOutput.hectoliter as number,
                  newDocumentOfOutput.calculationMethod as boolean,
                  settings,
                  TransactionType.Export,
                )[11],
            )
          : Math.round(
              srpsCalculation(
                categoryType,
                newDocumentOfOutput.brutto !== undefined &&
                  newDocumentOfOutput.tara !== undefined
                  ? newDocumentOfOutput.brutto - newDocumentOfOutput.tara
                  : 0,
                newDocumentOfOutput?.moisture ?? 0,
                newDocumentOfOutput.impurity ?? 0,
                newDocumentOfOutput.breakage ?? 0,
                newDocumentOfOutput.defect ?? 0,
                newDocumentOfOutput.hectoliter as number,
                newDocumentOfOutput.calculationMethod as boolean,
                settings,
                TransactionType.Export,
              )[0],
            );

      if (newDocumentOfOutput?.finishedDocumentFirstTime !== true) {
        copyOfNewDocumentOfOutput.date = new Date(); //ako nije zakljucena otpremnica prvi put,
        copyOfNewDocumentOfOutput.time = new Date(); //datum i vreme se formiraju kao trenutak zakljucenja otpremnice prvi put
      }

      copyOfNewDocumentOfOutput.status = ReceiveStatus.Finished; //zakljucena otpremnica, promenjen status u finished
      copyOfNewDocumentOfOutput.finishedDocumentFirstTime = true; //zakljucena otpremnica prvi put

      if (!copyOfNewDocumentOfOutput.warehouseman) {
        copyOfNewDocumentOfOutput.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
      }

      if (updating) {
        updateOneDocument(id as string, copyOfNewDocumentOfOutput).then(
          (response: AxiosResponse) => {
            if (response.status === 200 || response.status === 201) {
              //@ts-ignore
              toast.current.show({
                severity: "success",
                detail: t(`Uspešno popunjena otpremnica`),
                life: 3000,
              });
              navigate(`/mercantile-output-fast`);
              setOkButtonDisabled(false);
            } else if (response.status === 503) {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Sistemska greška: pokušaj snimanja otpremnice bez svog broja, pokušajte ponovo`,
                ),
                life: 3000,
              });
              setOkButtonDisabled(false);
            } else {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Dokument nije snimljen, proverite podatke o otpremi robe`,
                ),
                life: 3000,
              });
              setOkButtonDisabled(false);
            }
          },
        );
      } else {
        addNewDocument(copyOfNewDocumentOfOutput).then(
          (response: AxiosResponse) => {
            if (response.status === 200 || response.status === 201) {
              //@ts-ignore
              toast.current.show({
                severity: "success",
                detail: t(`Uspešno izmenjena otpremnica`),
                life: 3000,
              });
              navigate(`/mercantile-output-fast`);
              setOkButtonDisabled(false);
            } else {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Dokument nije izmenjen, proverite podatke o otpremi robe`,
                ),
                life: 3000,
              });
              setOkButtonDisabled(false);
            }
          },
        );
      }
    } else {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: error,
        life: 3000,
      });
      setOkButtonDisabled(false);
    }
  };

  const onError = () => {
    setOkButtonDisabled(true);
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
    setOkButtonDisabled(false);
  };

  const saveDraftVersion = () => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.status = ReceiveStatus.Arrived;

    if (!copyOfNewDocumentOfOutput.warehouseman) {
      copyOfNewDocumentOfOutput.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
    }

    //obavezno je izabrati proizvod
    if (newDocumentOfOutput.product && newDocumentOfOutput.product?.id) {
      //snimanje delimicno unetih podataka, bez validacije
      if (updating) {
        updateOneDocument(id as string, copyOfNewDocumentOfOutput).then(
          (response: AxiosResponse) => {
            console.log(response);
            //@ts-ignore
            toast.current.show({
              severity: "success",
              detail: t(`Uspešno popunjena otpremnica`),
              life: 3000,
            });
            navigate(`/mercantile-output-fast`);
          },
        );
      } else {
        addNewDocument(copyOfNewDocumentOfOutput).then(
          (response: AxiosResponse) => {
            if (response.status === 200 || response.status === 201) {
              //@ts-ignore
              toast.current.show({
                severity: "success",
                detail: t(`Uspešno izmenjena otpremnica`),
                life: 3000,
              });
              navigate(`/mercantile-output-fast`);
            } else {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Dokument nije izmenjen, proverite podatke o otpremi robe`,
                ),
                life: 3000,
              });
            }
          },
        );
      }

      navigate(`/mercantile-output-fast`);
    } else {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Nije izabran naziv robe`),
        life: 3000,
      });
    }
  };

  const handleResetForm = () => {
    setNewDocumentOfOutput({
      id: id ?? "",
      warehouse:
        activeUser?.role?.name === RoleType.Warehouseman
          ? (cloneDeep(activeUser.warehouse) as Warehouse)
          : ({} as Warehouse),
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      type: TransactionType.Export,
      date: new Date(),
      time: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      srps: undefined,
      product: {} as Product,
      productWithCode: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "",
      vehicleNumber: "",
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      calculationMethod: true,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "",
      dispositionId: "",
      finishedDocument: false,
      mercantile: true,
      input: false,
    });
  };

  const handleNewDocumentOfOutput = (prop: string, value: any) => {
    setNewDocumentOfOutput((newDocumentOfOutput) => ({
      ...newDocumentOfOutput,
      [prop]: value,
    }));
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.partner = partners.find(
      (item) => item.id == value,
    );
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleShipperChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.shipper = partners.find(
      (item) => item.id == value,
    );
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleProductOwnerChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.productOwner = partners.find(
      (item) => item.id == value,
    );
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    activeUser?.role?.name === RoleType.Warehouseman
      ? (copyOfNewDocumentOfOutput.warehouse = cloneDeep(
          activeUser.warehouse,
        ) as Warehouse)
      : (copyOfNewDocumentOfOutput.warehouse = warehouses.find(
          (item) => item.id == value,
        ) as Warehouse);
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategories.find(
      (item) => item.id == value,
    ) as ProductCategory;
    setProductCategory(copyOfNewProductCategory);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfOutput = cloneDeep(newDocumentOfOutput);
    copyOfNewDocumentOfOutput.product = products.find(
      (item) => item.id == value,
    ) as Product;
    setNewDocumentOfOutput(copyOfNewDocumentOfOutput);
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  if (buttonNewPartnerStyle) {
    addButtonNewPartnerStyle = buttonNewPartnerStyle;
  } else {
    addButtonNewPartnerStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewPartnerStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Otpremnica broj")}:`}{" "}
              {newDocumentOfOutput?.finishedDocumentNumber ??
                newDocumentOfOutput.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={"Sve otpremnice"}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/mercantile-output-fast`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti otpremnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfOutput.finishedDocument === true &&
                  newDocumentOfOutput.finishedDocumentId
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati otpremnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              {activeUser?.role?.name === RoleType.Warehouseman ? (
                <TextInput
                  id="warehouse"
                  value={activeUser?.warehouse?.name ?? ""}
                  onValueChange={handleWarehouseChange}
                  labelText={`${t("Magacin")} *`}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  disabled={activeUser?.role?.name === RoleType.Warehouseman}
                />
              ) : (
                <DropdownInput
                  id="warehouse"
                  value={newDocumentOfOutput.warehouse.id}
                  onValueChange={handleWarehouseChange}
                  optionValue="id"
                  optionLabel="name"
                  options={warehouses.filter((item) => item.virtual !== true)}
                  filter={false}
                  labelText={`${t("Magacin")} *`}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }} //@ts-ignore
                  disabled={activeUser?.role?.name === RoleType.Warehouseman}
                />
              )}
              <DateInput
                id="date"
                value={
                  newDocumentOfOutput?.finishedDocumentFirstTime
                    ? (newDocumentOfOutput.date as Date)
                    : new Date()
                }
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Datum")} *`}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                maxDate={new Date()}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={activeUser?.role?.name === RoleType.Warehouseman}
              />
              <TextInput
                id="warehouseman"
                value={newDocumentOfOutput.warehouseman as string}
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Magacioner")} *`}
                placeholderText={t("Unesite ime magacionera")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <DateInput
                id="time"
                value={
                  newDocumentOfOutput?.finishedDocumentFirstTime
                    ? (newDocumentOfOutput.time as Date)
                    : new Date()
                }
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Vreme")} *`}
                placeholderText={t("Izaberite vreme")}
                timeOnly={true}
                icon="bi bi-clock"
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={activeUser?.role?.name === RoleType.Warehouseman}
              />
              <DropdownInput
                id="partner"
                value={newDocumentOfOutput.partner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handlePartnerChange}
                options={partners}
                filter={true}
                labelText={`${t("Kupac")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <div className="new-partner-button">
                <NewPartnerForm
                  buttonLabel={`${t("Dodaj kupca")}`}
                  buttonIcon="bi bi-plus-square"
                  show={show}
                  buttonStyle={{
                    border: "none",
                    background: "rgba(21, 115, 71, 1)",
                    color: "#ffffff",
                    marginRight: "1rem",
                    margin: "0 1rem 0 0",
                  }}
                  buttonNewCityStyle={{
                    border: "none",
                    background: "rgba(21, 115, 71, 1)",
                  }}
                  buttonSaveStyle={{
                    border: "none",
                    background: "rgba(0, 165, 83, 1)",
                  }}
                  buttonInactiveStyle={{
                    border: "none",
                    background: "rgba(108, 117, 125, 1)",
                  }}
                />
              </div>
              <DropdownInput
                id="shipper"
                value={newDocumentOfOutput.shipper?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleShipperChange}
                options={partners}
                filter={true}
                labelText={t("Prevoznik")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <TextInput
                id="driverName"
                value={newDocumentOfOutput.driverName as string}
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Ime vozača")} *`}
                placeholderText={t("Unesite ime vozača")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <TextInput
                id="vehicleNumber"
                value={(
                  newDocumentOfOutput.vehicleNumber as string
                )?.toUpperCase()}
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Registarski broj vozila")} *`}
                placeholderText={t("Unesite registarski broj")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <TextInputWithIcon
                id="cellNumberFloor"
                value={newDocumentOfOutput.cellNumberFloor as string}
                onValueChange={handleNewDocumentOfOutput}
                labelText={t("Broj ćelije / podni magacin")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <DropdownInput
                id="productCategory"
                value={productCategory?.id}
                onValueChange={handleProductCategoryChange}
                optionValue="id"
                optionLabel="name"
                options={productCategories.filter(
                  (item) => item.type == ProductType.MercantileGoods,
                )}
                filter={false}
                labelText={`${t("Vrsta robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <DropdownInput
                id="productName"
                value={newDocumentOfOutput.product?.id}
                onValueChange={handleProductChange}
                optionValue="id"
                optionLabel="name"
                options={products.filter(
                  (item) => item.category?.id === productCategory?.id,
                )}
                filter={false}
                labelText={`${t("Naziv robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  productCategory === undefined ||
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished)
                }
              />
              <TextInputWithIcon
                id="brutto"
                value={(newDocumentOfOutput.brutto as number) ?? ""}
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Bruto")} *`}
                icon="bi bi-minecart-loaded"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a number",
                  },
                }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <TextInputWithIcon
                id="tara"
                value={(newDocumentOfOutput.tara as number) ?? ""}
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Tara")} *`}
                icon="bi bi-minecart"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a number",
                  },
                }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <TextInputWithIcon
                id="moisture"
                value={(newDocumentOfOutput.moisture as number) ?? ""}
                onValueChange={handleNewDocumentOfOutput}
                labelText={
                  newDocumentOfOutput.calculationMethod === true
                    ? `${t("Vlaga")} *`
                    : t("Vlaga")
                }
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={`${t("Unesite procenat vlage kao ceo broj, sa jednom ili dve decimale")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={
                  newDocumentOfOutput.calculationMethod === true
                    ? {
                        required: true,
                        pattern: {
                          value: /^\d+(\.\d{1,2})?$/,
                          message: "Please enter a number",
                        },
                      }
                    : { required: false }
                }
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <TextInputWithIcon
                id="impurity"
                value={newDocumentOfOutput.impurity as number}
                onValueChange={handleNewDocumentOfOutput}
                labelText={
                  newDocumentOfOutput.calculationMethod === true
                    ? `${t("Primese")} *`
                    : t("Primese")
                }
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={`${t("Unesite procenat primesa kao ceo broj, sa jednom ili dve decimale")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={
                  newDocumentOfOutput.calculationMethod === true
                    ? {
                        required: true,
                        pattern: {
                          value: /^\d+(\.\d{1,2})?$/,
                          message: "Please enter a number",
                        },
                      }
                    : { required: false }
                }
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              {newDocumentOfOutput?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="breakage"
                  value={(newDocumentOfOutput.breakage as number) ?? ""}
                  onValueChange={handleNewDocumentOfOutput}
                  labelText={
                    newDocumentOfOutput.calculationMethod === true
                      ? t("Lom *")
                      : t("Lom")
                  }
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite procenat loma kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newDocumentOfOutput.calculationMethod === true
                      ? {
                          required: true,
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: "Please enter a number",
                          },
                        }
                      : { required: false }
                  }
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished
                  }
                />
              )}
              {newDocumentOfOutput?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="defect"
                  value={(newDocumentOfOutput.defect as number) ?? ""}
                  onValueChange={handleNewDocumentOfOutput}
                  labelText={
                    newDocumentOfOutput.calculationMethod === true
                      ? t("Defekt *")
                      : t("Defekt")
                  }
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite procenat defekta kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newDocumentOfOutput.calculationMethod === true
                      ? {
                          required: true,
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: "Please enter a number",
                          },
                        }
                      : { required: false }
                  }
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished
                  }
                />
              )}
              {(newDocumentOfOutput?.product?.category?.name === "Pšenica" ||
                newDocumentOfOutput?.product?.category?.name === "Ječam") && (
                <TextInputWithIcon
                  id="hectoliter"
                  value={(newDocumentOfOutput.hectoliter as number) ?? ""}
                  onValueChange={handleNewDocumentOfOutput}
                  labelText={
                    newDocumentOfOutput.calculationMethod === true
                      ? `${t("Hektolitar")} *`
                      : t("Hektolitar")
                  }
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite hektolitar kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newDocumentOfOutput.calculationMethod === true
                      ? {
                          required: true,
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: "Please enter a number",
                          },
                        }
                      : { required: false }
                  }
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished
                  }
                />
              )}
              {newDocumentOfOutput?.product?.category?.name === "Pšenica" && (
                <TextInputWithIcon
                  id="protein"
                  value={(newDocumentOfOutput.protein as number) ?? ""}
                  onValueChange={handleNewDocumentOfOutput}
                  labelText={t("Protein")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite protein kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter a number",
                    },
                  }}
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished
                  }
                />
              )}
              {newDocumentOfOutput?.product?.category?.name === "Ječam" && (
                <div className="empty-field"></div>
              )}
              <RadioButtonInput
                id="foreignProduct"
                value={newDocumentOfOutput.foreignProduct}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleNewDocumentOfOutput}
                labelText={t("Tuđa roba na skladištenju")}
                control={control}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <RadioButtonInput
                id="calculationMethod"
                value={newDocumentOfOutput.calculationMethod}
                radioButtonValue1={t("Svodjenje na SRPS")}
                radioButtonValue2={t("Neto")}
                onValueChange={handleNewDocumentOfOutput}
                labelText={t("Metod obračuna")}
                control={control}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              {newDocumentOfOutput.foreignProduct === true && (
                <DropdownInput
                  id="productOwner"
                  value={newDocumentOfOutput.productOwner?.id as string}
                  optionValue="id"
                  optionLabel="name"
                  onValueChange={handleProductOwnerChange}
                  options={partners}
                  filter={true}
                  labelText={`${t("Vlasnik robe")}`}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished
                  }
                />
              )}
              {newDocumentOfOutput.foreignProduct === true && <div></div>}
              <TextInput
                id="loadingCityName"
                value={newDocumentOfOutput.loadingCityName as string}
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Mesto istovara")}`}
                placeholderText={t("Unesite mesto istovara")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <TextInput
                id="loadingContact"
                value={newDocumentOfOutput.loadingContact as string}
                onValueChange={handleNewDocumentOfOutput}
                labelText={`${t("Kontakt na istovaru")}`}
                placeholderText={t("Unesite kontakt na istovaru")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman &&
                  newDocumentOfOutput?.status === ReceiveStatus.Finished
                }
              />
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newDocumentOfOutput.note as string}
                  onValueChange={handleNewDocumentOfOutput}
                  rows={1}
                  cols={50}
                  labelText={t("Napomena")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished
                  }
                />
              </div>
              <RadioButtonInput
                id="finishedDocument"
                value={newDocumentOfOutput.finishedDocument}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleNewDocumentOfOutput}
                labelText={t("Zaključena otpremnica")}
                control={control}
                errors={errors}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished) ||
                  activeUser?.role?.name === RoleType.Logistician
                }
              />
              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={
                    newDocumentOfOutput.finishedDocument === true
                      ? handleSubmit(onSubmit, onError)
                      : saveDraftVersion
                  }
                  addStyle={addButtonSaveStyle}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfOutput?.status === ReceiveStatus.Finished) ||
                    okButtonDisabled
                  }
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfOutput?.status === ReceiveStatus.Finished
                  }
                />
              </div>
            </div>
            <div className="documents-preview">
              {!showPdf || !pdfData ? (
                <p>Učitavam PDF prikaz...</p>
              ) : (
                <PdfPreview
                  pdfDefinition={pdfData}
                  downloadDocument={downloadPdfDocument}
                  printDocument={printPdfDocument}
                  documentName={`Otpremnica_${newDocumentOfOutput.finishedDocumentId ?? ""}`}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
